export const COOKIE_CONSENT_KEY = 'jamsom_cookie_consent';
export const COOKIE_CONSENT_EVENT = 'jamsom:cookie-consent-changed';
export const COOKIE_SETTINGS_EVENT = 'openCookieSettings';
export const COOKIE_CONSENT_DURATION_MS = 1000 * 60 * 60 * 24 * 183;
export const MAX_BROWSER_TIMEOUT_MS = 2_147_483_647;
const COOKIE_CONSENT_VERSION = 1;
let persistenceFailed = false;

export type ConsentStatus = 'accepted' | 'necessary';

type StoredConsent = {
  version: number;
  status: ConsentStatus;
  createdAt: number;
  expiresAt: number;
};

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function removeStoredConsent() {
  try {
    storage()?.removeItem(COOKIE_CONSENT_KEY);
  } catch {
    // A denied storage permission must also fail closed.
  }
}

function getStoredConsent(): StoredConsent | null {
  if (persistenceFailed) return null;
  try {
    const value = storage()?.getItem(COOKIE_CONSENT_KEY);
    if (!value) return null;
    const consent = JSON.parse(value) as StoredConsent;
    if (
      consent.version !== COOKIE_CONSENT_VERSION ||
      (consent.status !== 'accepted' && consent.status !== 'necessary') ||
      !Number.isFinite(consent.createdAt) ||
      !Number.isFinite(consent.expiresAt) ||
      consent.createdAt > Date.now() ||
      consent.expiresAt !== consent.createdAt + COOKIE_CONSENT_DURATION_MS ||
      consent.expiresAt <= Date.now()
    ) {
      removeStoredConsent();
      return null;
    }
    return consent;
  } catch {
    removeStoredConsent();
    return null;
  }
}

/** Returns valid consent only. Missing, malformed and expired values reject analytics. */
export function getCookieConsent(): ConsentStatus | null {
  return getStoredConsent()?.status ?? null;
}

export function getCookieConsentExpiry(): number | null {
  return getStoredConsent()?.expiresAt ?? null;
}

/** Browser timers max out at ~24.8 days; callers reschedule until this expiry. */
export function getConsentExpiryDelay(expiresAt: number) {
  return Math.min(Math.max(expiresAt - Date.now(), 0), MAX_BROWSER_TIMEOUT_MS);
}

export function hasAnalyticsConsent() {
  return getCookieConsent() === 'accepted';
}

export function setCookieConsent(status: ConsentStatus) {
  const createdAt = Date.now();
  const expiresAt = createdAt + COOKIE_CONSENT_DURATION_MS;
  try {
    const target = storage();
    if (!target) throw new Error('Consent storage unavailable');
    target.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      version: COOKIE_CONSENT_VERSION,
      status,
      createdAt,
      expiresAt,
    }));
    persistenceFailed = false;
  } catch {
    persistenceFailed = true;
    try { storage()?.removeItem(COOKIE_CONSENT_KEY); } catch { /* browser restriction */ }
    // A failed write must not leave an older acceptance effective on this page.
  }
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: { status, expiresAt } }));
}

/** Removes browser storage that GA4 and PostHog can create on this origin. */
export function clearAnalyticsStorage() {
  if (typeof window === 'undefined') return;

  try {
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (key && (/^ph_/i.test(key) || /posthog/i.test(key))) window.localStorage.removeItem(key);
    }
  } catch {
    // Storage can be disabled by browser settings.
  }

  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => /^(?:_ga|_gid|_gat|ph_|posthog)/i.test(name));
  for (const name of cookieNames) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    const parentDomain = window.location.hostname.split('.').slice(-2).join('.');
    if (parentDomain.includes('.')) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${parentDomain}; SameSite=Lax`;
    }
  }
}
